import React, { useEffect } from "react";
import { CellContext } from "@tanstack/react-table";

import { FiltersBackground, FormFiltersBackground, PageMain } from "components/molecules";
import { useQuery } from "@tanstack/react-query";
import { QUERIES_KEYS } from "queries";
import { CourseFinancialReportRow, reportsApi } from "api/reportsApi";
import { PaziaDateSelect } from "components/atoms";
import { TanTableClient, useFilters, TanTableColumn } from "components/molecules";
import { toMoneyFormat } from "utils";
import { PaziaBreadcrumbs } from "components/atoms";
import { routes } from "consts";
import { useNavigate } from "react-router-dom";

export const CoursesFinancialReport = () => {
  const navigate = useNavigate();
  const { filter, changeFilter } = useFilters();
  const { data, isLoading, refetch } = useQuery({
    queryKey: QUERIES_KEYS.REPORTS.COURSES_FINANCIAL(),
    queryFn: () => reportsApi.getCoursesFinancialReport(filter),
  });

  useEffect(() => {
    refetch();
  }, [filter]);

  const columns: TanTableColumn<CourseFinancialReportRow>[] = [
    {
      id: "courseName",
      accessorKey: "courseName",
      header: "Kurs",
      size: 500,
      cell: (info) => (
        <span
          onClick={(e) => {
            e.stopPropagation();
            navigate(routes.COURSES_MANAGE_NESTED.FINANCES(info.row.original.courseId));
          }}
          className="cursor-pointer hover:underline"
        >
          {info.getValue<string>()}
        </span>
      ),
    },
    {
      id: "totalIncome",
      accessorKey: "totalIncome",
      header: "Przychód",
      cell: (info) => <MoneyCell info={info} />,
    },
    {
      id: "totalExpense",
      accessorKey: "totalExpense",
      header: "Koszty",
      cell: (info) => <MoneyCell info={info} />,
    },
    {
      id: "netIncome",
      accessorKey: "netIncome",
      header: "Zysk",
      cell: (info) => <MoneyCell info={info} />,
    },
  ];

  const onFromDateChange = (date: Date | null) =>
    changeFilter("from", date ? `createdDate ge ${date.toISOString()}` : "");

  const onToDateChange = (date: Date | null) => changeFilter("to", date ? `createdDate le ${date.toISOString()}` : "");

  const tableData = data?.data ?? [];
  return (
    <PageMain>
      <PaziaBreadcrumbs
        segments={[{ title: "Raporty", path: routes.REPORTS }, { title: "Zestawienie finansowe kursów" }]}
      />
      <FormFiltersBackground>
        <div className="flex flex-wrap items-center gap-2">
          <PaziaDateSelect name="dateFrom" label="Data od" onChange={onFromDateChange} />
          <PaziaDateSelect name="dateTo" label="Data od" onChange={onToDateChange} />
        </div>
      </FormFiltersBackground>
      <div className="mt-4">
        <TanTableClient data={tableData} columns={columns} />
      </div>
    </PageMain>
  );
};

const MoneyCell = ({ info }: { info: CellContext<CourseFinancialReportRow, unknown> }) => {
  const value = info.getValue<number>();
  const valueClassname = value < 0 ? "text-red-500" : "text-green-500";
  return <span className={valueClassname}>{toMoneyFormat(info.getValue<number>(), true)}</span>;
};
